import {ColorSchemeType, Flag, Tooltip} from 'platform/components';
import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import {AdvertisementStatusEnum, type SalesPlatformsResBody} from '@dms/api/sales';
import i18n from '@dms/i18n';

type TStatusItem = {
  color: ColorSchemeType;
  text: string;
};

type StatusItemProps = {
  currentStatus: AdvertisementStatusEnum | `${AdvertisementStatusEnum}`;
  statusDescription: string | null;
  platformCode: string;
  publicationStatus?: SalesPlatformsResBody['publicationStatus'];
};

export const StatusItem = (props: StatusItemProps) => {
  const preferLifecycleStatus = match(props.currentStatus)
    .with(
      AdvertisementStatusEnum.PUBLISH_FAILED,
      AdvertisementStatusEnum.UPDATE_FAILED,
      AdvertisementStatusEnum.UNPUBLISH_FAILED,
      always(true)
    )
    .with(
      AdvertisementStatusEnum.PUBLISHING,
      AdvertisementStatusEnum.UPDATING,
      AdvertisementStatusEnum.UNPUBLISHING,
      () => props.publicationStatus === 'published'
    )
    .otherwise(always(false));
  const statusItemValue = match(
    preferLifecycleStatus ? props.currentStatus : (props.publicationStatus ?? props.currentStatus)
  )
    .with(
      'pending-approval',
      always<TStatusItem>({
        color: 'blue',
        text: i18n.t('entity.orderIntegration.labels.pendingApproval'),
      })
    )
    .with(
      'rejected',
      always<TStatusItem>({
        color: 'red',
        text: i18n.t('entity.orderIntegration.labels.rejected'),
      })
    )
    .with(
      'withdrawn',
      AdvertisementStatusEnum.NOT_PUBLISHED,
      AdvertisementStatusEnum.UNKNOWN,
      always<TStatusItem>({
        color: 'neutral',
        text:
          props.publicationStatus === 'unknown'
            ? i18n.t('entity.condition.labels.statusUnknown')
            : i18n.t('page.advertisement.actions.notPublished'),
      })
    )
    .with(
      AdvertisementStatusEnum.PUBLISHING,
      always<TStatusItem>({
        color: 'blue',
        text: `${i18n.t('page.advertisement.actions.publishing')}...`,
      })
    )
    .with(
      AdvertisementStatusEnum.PUBLISHED,
      always<TStatusItem>({
        color: 'green',
        text: match(props.platformCode)
          .with('tipcars', () => i18n.t('page.advertisement.actions.transferred'))
          .with('fastback-b2b', () => i18n.t('general.labels.sent'))
          .otherwise(() => i18n.t('page.advertisement.actions.published')),
      })
    )
    .with(
      AdvertisementStatusEnum.UPDATING,
      always<TStatusItem>({
        color: 'blue',
        text: `${i18n.t('page.advertisement.actions.updating')}...`,
      })
    )
    .with(
      AdvertisementStatusEnum.UNPUBLISHING,
      always<TStatusItem>({
        color: 'blue',
        text: `${i18n.t('page.advertisement.actions.unPublishing')}...`,
      })
    )
    .with(
      AdvertisementStatusEnum.PUBLISH_FAILED,
      AdvertisementStatusEnum.UNPUBLISH_FAILED,
      AdvertisementStatusEnum.UPDATE_FAILED,
      always<TStatusItem>({
        color: 'red',
        text: i18n.t('general.actions.error'),
      })
    )
    .otherwise(always(null));

  return (
    <Tooltip placement="top" label={props.statusDescription}>
      <Show when={statusItemValue}>
        <Flag
          colorScheme={statusItemValue?.color}
          data-testid={`statusText-advertisement.platforms.${props.platformCode}`}
          label={statusItemValue?.text ?? '-'}
          isSubtle
        />
      </Show>
    </Tooltip>
  );
};
